// MarketTable — the responsive odds-table primitive for the market family
// (Phase 3). Shared by Match Goals + Match Cards (and, later, Winner 1x2 /
// Player markets via their own row shapes).
//
// Legacy renders two layouts off a measured `wrapper.clientWidth`: **wide**
// (odds + implied % inline per column) and **narrow** (stacked), each with an
// optional **Best** column gated at a lower width. A ResizeObserver re-picks the
// layout on resize. React owns the whole body: a useLayoutEffect measures the
// wrapper (before paint, so no wrong-first-paint), sets the layout state, and
// re-measures via a ResizeObserver (disconnected on cleanup). Rows carry the
// `data-line` attribute so the legacy cross-module highlight sync
// (`syncMarketHighlight` in main.js — stays legacy until Phase 4) keeps working
// on the React-rendered DOM. Emits the SAME class names + DOM as the legacy
// renderer (verified by the visual A/B). Tags the enclosing `.placed-module`
// with `betting-module` for the dark betting theme.
//
// SINGLE TEXT NODE per run: each implied-% is one template literal so the glyph
// run matches the legacy's single innerHTML text node (see the A/B gotcha in
// docs/architecture.md).

import { useLayoutEffect, useRef, useState } from "react";
import type { MarketRow } from "@bento/shared";

export interface MarketTableProps {
  /** Wrapper class, e.g. "gm-wrapper" (Goals) / "cm-wrapper" (Cards). */
  wrapperClass: string;
  rows: MarketRow[];
  /** clientWidth ≥ this → wide layout (default 360, matching the legacy). */
  wideAt?: number;
  /** clientWidth ≥ this → show the Value + Best columns (default 230). */
  showBestAt?: number;
}

function BestCell({
  colCls,
  best,
}: {
  colCls: string;
  best: MarketRow["best"];
}) {
  return (
    <div className={`${colCls} bm-best-cell`}>
      <span className="bm-txt">{best.odds.toFixed(2)}</span>
      <div className="bm-badge" style={{ background: best.colour }}>
        <span className="bm-badge-text">{best.abbr}</span>
      </div>
    </div>
  );
}

export default function MarketTable({
  wrapperClass,
  rows,
  wideAt = 360,
  showBestAt = 230,
}: MarketTableProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ wide: false, showBest: false });

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const mod = wrapper.closest<HTMLElement>(".placed-module");
    mod?.classList.add("betting-module");

    const measure = () => {
      const w = wrapper.clientWidth;
      setLayout((prev) => {
        const next = { wide: w >= wideAt, showBest: w >= showBestAt };
        return prev.wide === next.wide && prev.showBest === next.showBest
          ? prev
          : next;
      });
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(wrapper);
    }
    return () => {
      ro?.disconnect();
      mod?.classList.remove("betting-module");
    };
  }, [wideAt, showBestAt]);

  const { wide, showBest } = layout;
  // Column class suffix flips w/n between the two layouts.
  const c = wide
    ? { lbl: "bmc-lbl-w", cell: "bmc-cell-w", val: "bmc-val-w", best: "bmc-best-w" }
    : { lbl: "bmc-lbl-n", cell: "bmc-cell-n", val: "bmc-val-n", best: "bmc-best-n" };

  return (
    <div className={`${wrapperClass} bm-wrapper`} ref={wrapperRef}>
      <div className="bm-hdr">
        <div className={c.lbl} />
        <div className={`${c.cell} bm-hdr-cell`}>MARKET</div>
        <div className={`${c.cell} bm-hdr-cell`}>Model</div>
        {showBest && <div className={`${c.val} bm-hdr-cell`}>Value</div>}
        {showBest && (
          <div className={`${c.best} bm-hdr-cell bm-right`}>
            {wide ? "Best Price" : "Best"}
          </div>
        )}
      </div>
      <div className="bm-scroll">
        {rows.map((r) => (
          <div
            key={r.dataLine}
            data-line={r.dataLine}
            className={`bm-row${r.alt ? " bm-row--alt" : ""}`}
          >
            <div className={`${c.lbl} bm-lbl`}>{r.label}</div>
            {wide ? (
              <>
                <div className={`${c.cell} bm-cell-inline`}>
                  <span className="bm-txt">{r.mktOdds.toFixed(2)}</span>{" "}
                  <span className="bm-prob">{`(${r.mktImp.toFixed(1)}%)`}</span>
                </div>
                <div className={`${c.cell} bm-cell-inline`}>
                  <span className="bm-txt">{r.modelOdds.toFixed(2)}</span>{" "}
                  <span className="bm-prob">{`(${r.modelImp.toFixed(1)}%)`}</span>
                </div>
              </>
            ) : (
              <>
                <div className={`${c.cell} bm-cell-stack`}>
                  <span className="bm-txt">{r.mktOdds.toFixed(2)}</span>
                  <span className="bm-prob">{`${r.mktImp.toFixed(1)}%`}</span>
                </div>
                <div className={`${c.cell} bm-cell-stack`}>
                  <span className="bm-txt">{r.modelOdds.toFixed(2)}</span>
                  <span className="bm-prob">{`${r.modelImp.toFixed(1)}%`}</span>
                </div>
              </>
            )}
            {showBest && (
              <div
                className={`${c.val} bm-txt bm-center`}
                style={{ color: r.edgeCol }}
              >
                {r.edgeStr}
              </div>
            )}
            {showBest && <BestCell colCls={c.best} best={r.best} />}
          </div>
        ))}
      </div>
    </div>
  );
}
